import { createTheme } from "@mui/material/styles";

import {
  bone,
  brandRed,
  ink,
  paper,
  redDeep,
  smoke,
  stone,
} from "../routes/pricing-reports/design/designTokens";

const outlinedInputOverrides = {
  root: {
    borderRadius: 0,
    backgroundColor: paper,
    fontFamily: '"Archivo", sans-serif',
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: stone,
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: brandRed,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: brandRed,
      borderWidth: 1,
    },
  },
};

const insightsThemeOptions = {
  typography: {
    fontFamily: '"Archivo", sans-serif',
    h2: {
      fontSize: "2rem",
      fontWeight: 800,
      lineHeight: 1.15,
      letterSpacing: "-0.005em",
      color: ink,
    },
    h3: {
      fontSize: "1.5rem",
      fontWeight: 800,
      lineHeight: 1.15,
      letterSpacing: "-0.005em",
      color: ink,
    },
    h4: {
      fontSize: "1.25rem",
      fontWeight: 700,
      lineHeight: 1.15,
      letterSpacing: "-0.005em",
      color: ink,
    },
    h5: {
      fontSize: "1.125rem",
      fontWeight: 700,
      lineHeight: 1.2,
      color: ink,
    },
    h6: {
      fontSize: "1rem",
      fontWeight: 700,
      lineHeight: 1.2,
      color: ink,
    },
    body1: {
      fontSize: "1rem",
      fontWeight: 400,
      lineHeight: 1.45,
      letterSpacing: "-0.01em",
      color: ink,
    },
    body2: {
      fontSize: "0.875rem",
      fontWeight: 400,
      lineHeight: 1.45,
      letterSpacing: "-0.008em",
      color: ink,
    },
    caption: {
      fontSize: "0.75rem",
      fontWeight: 400,
      lineHeight: 1.45,
      letterSpacing: "-0.005em",
      color: ink,
    },
    subtitle1: {
      fontSize: "1rem",
      fontWeight: 400,
      lineHeight: 1.45,
      letterSpacing: "-0.01em",
      color: ink,
    },
    subtitle2: {
      fontSize: "0.875rem",
      fontWeight: 400,
      lineHeight: 1.45,
      letterSpacing: "-0.008em",
      color: ink,
    },
    button: {
      fontSize: "0.875rem",
      fontWeight: 700,
      lineHeight: 1.45,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      color: ink,
    },
    overline: {
      fontSize: "0.75rem",
      fontWeight: 400,
      lineHeight: 1.45,
      letterSpacing: "0.05em",
      textTransform: "uppercase",
      color: ink,
    },
  },
  palette: {
    mode: "light",
    primary: {
      main: brandRed,
      dark: redDeep,
      contrastText: paper,
    },
    secondary: {
      main: "#efeeee",
      contrastText: "#000000",
    },
    text: {
      primary: ink,
      secondary: smoke,
    },
    background: {
      default: paper,
      paper,
    },
    divider: stone,
  },
  shape: {
    borderRadius: 0,
  },
  components: {
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: 0,
          textTransform: "none",
          fontSize: "0.875rem",
          fontFamily: '"Archivo", sans-serif',
          "&.MuiButton-root": {
            fontFamily: '"Archivo", sans-serif',
          },
        },
        containedPrimary: {
          backgroundColor: brandRed,
          color: paper,
          "&:hover": {
            backgroundColor: redDeep,
          },
        },
        outlinedSecondary: {
          color: "#000",
          backgroundColor: "#efeeee",
          borderColor: "#e0e0e0",
          "&:hover": {
            backgroundColor: "#e0e0e0",
            borderColor: "#e0e0e0",
          },
        },
        containedSecondary: {
          backgroundColor: "#efeeee",
          color: "#000",
          "&:hover": {
            backgroundColor: "#e0e0e0",
          },
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          textTransform: "none",
          fontFamily: '"Archivo", sans-serif',
          color: ink,
          borderColor: stone,
          "&.Mui-selected": {
            backgroundColor: brandRed,
            color: paper,
            "&:hover": {
              backgroundColor: redDeep,
            },
          },
        },
      },
    },
    MuiToggleButtonGroup: {
      styleOverrides: {
        root: {
          borderRadius: 0,
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          color: ink,
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: 48,
          borderBottom: `1px solid ${stone}`,
        },
        indicator: {
          height: 2,
          borderRadius: 0,
          backgroundColor: brandRed,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          minHeight: 48,
          fontFamily: '"Archivo", sans-serif',
          textTransform: "none",
          fontWeight: 600,
          color: smoke,
          border: `1px solid transparent`,
          borderBottom: `1px solid ${stone}`,
          marginBottom: -1,
          "&.Mui-selected": {
            color: ink,
            backgroundColor: bone,
            border: "none",
            borderBottom: "none",
          },
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          marginTop: 16,
          marginBottom: 0,
          border: `2px solid ${stone}`,
          borderRadius: 0,
          backgroundColor: paper,
          boxShadow: "none",
          overflow: "hidden",
          "&:before": {
            display: "none",
          },
          "&:not(.Mui-expanded)": {
            backgroundColor: paper,
            borderColor: stone,
          },
          "&:last-of-type": {
            marginBottom: 0,
          },
          "&.Mui-expanded": {
            marginTop: 16,
            marginBottom: 0,
            backgroundColor: bone,
            borderColor: stone,
          },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          minHeight: 48,
          backgroundColor: paper,
          color: ink,
          "&:not(.Mui-expanded)": {
            backgroundColor: paper,
          },
          "&.Mui-expanded": {
            minHeight: 48,
            backgroundColor: bone,
            color: ink,
          },
        },
        content: {
          margin: "12px 0",
          "&.Mui-expanded": {
            margin: "12px 0",
          },
        },
        expandIconWrapper: {
          color: smoke,
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          padding: "16px",
          borderTop: `1px solid ${stone}`,
          backgroundColor: bone,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 0,
          border: `1px solid ${stone}`,
          backgroundColor: paper,
          boxShadow: "none",
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          backgroundColor: bone,
          borderBottom: `1px solid ${stone}`,
          fontFamily: '"Archivo", sans-serif',
          fontWeight: 800,
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          backgroundColor: paper,
          fontFamily: '"Archivo", sans-serif',
        },
        dividers: {
          borderTop: `1px solid ${stone}`,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: outlinedInputOverrides,
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: smoke,
          fontFamily: '"Archivo", sans-serif',
          "&.Mui-focused": {
            color: ink,
          },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: smoke,
          fontFamily: '"Archivo", sans-serif',
          "&.Mui-focused": {
            color: ink,
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          color: smoke,
          fontFamily: '"Archivo", sans-serif',
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: "outlined",
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          backgroundColor: paper,
          color: ink,
          border: `1px solid ${stone}`,
          borderRadius: 0,
        },
        option: {
          fontFamily: '"Archivo", sans-serif',
          '&[aria-selected="true"].Mui-focused': {
            backgroundColor: stone,
          },
          '&[aria-selected="true"]': {
            backgroundColor: stone,
          },
          "&.Mui-focused": {
            backgroundColor: stone,
          },
        },
        tag: {
          borderRadius: 0,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          border: `1px solid ${stone}`,
          backgroundColor: paper,
          color: ink,
          fontFamily: '"Archivo", sans-serif',
        },
        deleteIcon: {
          color: smoke,
          "&:hover": {
            color: ink,
          },
        },
      },
    },
  },
};

const baseTheme = createTheme(insightsThemeOptions);

const insightsTheme = createTheme(baseTheme, {
  typography: {
    h1: {
      ...baseTheme.typography.h1,
      fontWeight: 800,
      lineHeight: 1.02,
      letterSpacing: "-0.01em",
      color: baseTheme.palette.text.primary,
      fontSize: "2rem",
      [baseTheme.breakpoints.up("md")]: {
        fontSize: "2.75rem",
      },
    },
  },
});

export default insightsTheme;
